import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { DialogHeader, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { useCallback, useMemo } from "react";
import { useState } from "react";
import { createUser } from "../../lib/api";
import { validateEmail } from "../../lib/utils";
import { Checkbox } from "../ui/checkbox";
import { SubscriptionDisplayName, SubscriptionEnum, User } from "../../lib/types";


function CreateUser({refreshUsers}: {refreshUsers: () => void}) { 
  const [openCreateUserDialog, setOpenCreateUserDialog] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userTokens, setUserTokens] = useState<number | null>(null);
  const [userCreationError, setUserCreationError] = useState<string>('');
  const [userCreationSuccess, setUserCreationSuccess] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true); 
  const [subscriptions, setSubscriptions] = useState<{subscription: SubscriptionEnum, checked: boolean}[]>(
    Object.values(SubscriptionEnum).map((subscription: SubscriptionEnum) => ({subscription: subscription, checked: subscription === SubscriptionEnum.RESEARCH ? true : false}))
  );

  const resetCreateUserState = useCallback(() => {
    setUserEmail("");
    setUserName("");
    setUserTokens(null);
    setSubscriptions(Object.values(SubscriptionEnum).map((subscription: SubscriptionEnum) => ({subscription: subscription, checked: subscription === SubscriptionEnum.RESEARCH ? true : false})));
  }, []);

  const handleOpenCreateUserDialog = useCallback((open: boolean) => {
    resetCreateUserState();
    setUserCreationSuccess(null);
    setOpenCreateUserDialog(open);
  }, [resetCreateUserState]);

  const handleSubscriptionChange = useCallback((subscription: SubscriptionEnum, checked: boolean) => {
    setSubscriptions(prev => {
      const prevSubscriptions = [...prev];
      const subscriptionIndex = prevSubscriptions.findIndex((row) => row.subscription === subscription);
      prevSubscriptions[subscriptionIndex] = {...prevSubscriptions[subscriptionIndex], checked: checked};
      return prevSubscriptions;
    });
  }, [subscriptions]);

    const handleUserCreate = useCallback(async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setUserCreationError("");

        // Validate email before proceeding
        if (!validateEmail(userEmail)) {
            setIsEmailValid(false); 
            setLoading(false); 
            return; 
        } else {
            setIsEmailValid(true);
        }

        const subscriptionsString = subscriptions.filter(
            (subscription) => subscription.checked).map(
            (subscription) => subscription.subscription).join(',');

        try {
            const response = await createUser(userEmail, userName, userTokens, subscriptionsString);
            
            if (response) {
                setUserCreationSuccess(response as User);
                refreshUsers();
                resetCreateUserState();
            }
        } catch (error) {
            console.error("Error creating user:", error);
            setUserCreationError("Error creating user");
        } finally {
            setLoading(false);
        }
    }, [userEmail, userName, refreshUsers, userTokens, subscriptions, resetCreateUserState]);

    const userCreationDetailsComponent = useMemo(() => {
      return userCreationSuccess !== null ?
      ( <div>
            <h2 className="text-md font-bold text-green-500">User Created successfully</h2>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="id" className="text-right text-gray-500">
                ID
              </Label>
              <Input id="id" value={userCreationSuccess.user_id} className="col-span-3 text-gray-500" readOnly/>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right text-gray-500">
                Name
              </Label>
              <Input id="name" value={userCreationSuccess.user_name} className="col-span-3 text-gray-500" readOnly/>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="useremail" className="text-right text-gray-500">
                Email
              </Label>
              <Input id="useremail" value={userCreationSuccess.user_email} className="col-span-3 text-gray-500" readOnly/>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="usertokens" className="text-right text-gray-500">
                Tokens
              </Label>
              <Input id="usertokens" type='number' value={userCreationSuccess.user_tokens} className="col-span-3 text-gray-500" readOnly />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="subscriptions" className="text-right text-gray-500">
                Subscriptions
              </Label>
              <Input id="subscriptions" value={userCreationSuccess.subscriptions} className="col-span-3 text-gray-500" readOnly />
            </div>
        </div>) 
      : null;
    }, [userCreationSuccess]);

  return (
    <Dialog open={openCreateUserDialog} onOpenChange={handleOpenCreateUserDialog}>
        <DialogTrigger asChild className="ml-auto">
            <Button variant="outline">Create User</Button>
        </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>Create User</DialogTitle>
                <DialogDescription className="hidden">
                Allows new to be created
                </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">
                    EMail
                </Label>
                <Input id="email" value={userEmail} onChange={(e) => { 
                    setUserEmail(e.target.value);
                    setIsEmailValid(validateEmail(e.target.value));
                    }}
                    className={`col-span-3 ${isEmailValid ? '' : 'border-red-500'}`} />
                </div>
                {!isEmailValid && <p className="text-red-500 text-sm text-center">Invalid email address.</p>}
                <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                    Name
                </Label>
                <Input id="name" value={userName} onChange={(e) => setUserName(e.target.value)} className="col-span-3" />
                </div>
                <div className="grid grid-cols-4 items-center gap-4 row-span-2">
                <div className="col-span-1 row-span-2 justify-end text-right"> 
                    <Label>
                    Subscriptions
                    </Label>
                </div>
                <div className="col-span-3 gap-1 row-span-2">
                  {Object.values(SubscriptionEnum).map((subscription: SubscriptionEnum) => (
                    <div className="col-span-3 space-x-2 row-span-2">
                    <Checkbox id={subscription} checked={subscriptions.filter(
                        (s) => s.subscription === subscription)[0]?.checked || false} 
                        onCheckedChange={(checked: boolean) => handleSubscriptionChange(subscription, checked)}/>
                    <Label htmlFor={subscription}>{SubscriptionDisplayName[subscription]}</Label>
                    </div>
                  ))}
                </div>
                </div>
                <div className="text-red-500" hidden={subscriptions.filter(
                (subscription) => subscription.checked).length > 0}>
                Atleast one subscription is required
                </div>
            </div>
            <DialogFooter className="sm:justify-start flex flex-col gap-2">
                <Button onClick={handleUserCreate} disabled={loading}>Create</Button>
                {userCreationError && <p className="text-red-500">{userCreationError}</p>}
            </DialogFooter>
            {userCreationDetailsComponent}
      </DialogContent>
    </Dialog>
  )
}

export default CreateUser;
